// Selecting in the sheet (Leon, 2026-10-23, mockup v13): the unit is a ROW —
// a booking. Click a row to select it, drag down for more, Shift+click for a
// range, Ctrl+click to add or remove one. Only a drag that crosses into
// another column becomes a box of cells, as in Google Sheets.
//
// With rows selected, a column's value list works ON the selection: untick
// Cash and the selected Cash rows are deselected; tick it and every shown Cash
// row joins (Leon: "that's common sense").
//
// Rows are indexes into the rows on screen; `ids` maps an index to a row id,
// so a selection survives sorting and filtering. Pure, so tested.

import { asNumber, cellText, type SheetColumn, type SheetRow, tsvCell } from "./sheet-table.model";

export interface CellRef {
  r: number;
  c: number;
}

export interface RangeBox {
  r0: number;
  r1: number;
  c0: number;
  c1: number;
}

export interface SheetSelection {
  /** Selected rows, by id. Empty while a box of cells is selected. */
  rows: ReadonlySet<string>;
  /** A box of cells — only after dragging across columns. */
  box: { anchor: CellRef; focus: CellRef } | null;
  /** Where Shift+click and Shift+arrows extend from. */
  anchor: number;
  /** The outlined cell; arrows move it. */
  active: CellRef | null;
}

export const EMPTY_SELECTION: SheetSelection = {
  rows: new Set(),
  box: null,
  anchor: 0,
  active: null,
};

export interface Modifiers {
  shift: boolean;
  ctrl: boolean;
}

const span = (ids: readonly string[], a: number, b: number) =>
  ids.slice(Math.max(0, Math.min(a, b)), Math.max(a, b) + 1);

/** Pressing a cell (or a row number, as column 0). */
export function pressCell(
  selection: SheetSelection,
  r: number,
  c: number,
  modifiers: Modifiers,
  ids: readonly string[],
): SheetSelection {
  const id = ids[r];
  if (id === undefined) return selection;
  if (modifiers.ctrl) {
    const rows = new Set(selection.box ? [] : selection.rows);
    if (rows.has(id)) rows.delete(id);
    else rows.add(id);
    return { rows, box: null, anchor: r, active: { r, c } };
  }
  if (modifiers.shift) {
    // Adds the range to what is already picked, so Ctrl-picked rows stay.
    const rows = new Set([
      ...(selection.box ? [] : selection.rows),
      ...span(ids, selection.anchor, r),
    ]);
    return { rows, box: null, anchor: selection.anchor, active: { r, c } };
  }
  // Clicking the only selected row again deselects it, as most tables do.
  if (!selection.box && selection.rows.size === 1 && selection.rows.has(id)) {
    return { ...EMPTY_SELECTION, anchor: r, active: { r, c } };
  }
  return { rows: new Set([id]), box: null, anchor: r, active: { r, c } };
}

/** Dragging from `start`: down the same column selects rows; crossing a column draws a box. */
export function dragTo(
  selection: SheetSelection,
  start: CellRef,
  r: number,
  c: number,
  ids: readonly string[],
): SheetSelection {
  if (c !== start.c || selection.box) {
    return {
      rows: new Set(),
      box: { anchor: start, focus: { r, c } },
      anchor: start.r,
      active: { r, c },
    };
  }
  return { rows: new Set(span(ids, start.r, r)), box: null, anchor: start.r, active: { r, c } };
}

/** Arrow keys: up/down moves the selected row (Shift extends it); left/right moves the outline. */
export function moveActive(
  selection: SheetSelection,
  dr: number,
  dc: number,
  extend: boolean,
  ids: readonly string[],
  cols: number,
): SheetSelection {
  if (ids.length === 0 || cols === 0) return selection;
  const from = selection.active ?? { r: 0, c: 0 };
  const r = Math.max(0, Math.min(ids.length - 1, from.r + dr));
  const c = Math.max(0, Math.min(cols - 1, from.c + dc));
  if (dr === 0) return { ...selection, active: { r, c } };
  if (extend) {
    return {
      rows: new Set(span(ids, selection.anchor, r)),
      box: null,
      anchor: selection.anchor,
      active: { r, c },
    };
  }
  return { rows: new Set([ids[r] as string]), box: null, anchor: r, active: { r, c } };
}

/** Space: tick or untick the active row. */
export function toggleActiveRow(selection: SheetSelection, ids: readonly string[]): SheetSelection {
  const r = selection.active?.r;
  if (r === undefined || ids[r] === undefined) return selection;
  return pressCell(selection, r, selection.active?.c ?? 0, { shift: false, ctrl: true }, ids);
}

export function selectAll(ids: readonly string[]): SheetSelection {
  return { rows: new Set(ids), box: null, anchor: 0, active: ids.length ? { r: 0, c: 0 } : null };
}

export function withRows(
  selection: SheetSelection,
  ids: readonly string[],
  on: boolean,
): SheetSelection {
  const rows = new Set(selection.box ? [] : selection.rows);
  for (const id of ids) {
    if (on) rows.add(id);
    else rows.delete(id);
  }
  return { ...selection, rows, box: null };
}

/** The box, clamped to what is on screen; null when the selection is rows. */
export function boxOf(selection: SheetSelection, rows: number, cols: number): RangeBox | null {
  if (!selection.box || rows === 0 || cols === 0) return null;
  const clampR = (value: number) => Math.max(0, Math.min(rows - 1, value));
  const clampC = (value: number) => Math.max(0, Math.min(cols - 1, value));
  const { anchor, focus } = selection.box;
  return {
    r0: clampR(Math.min(anchor.r, focus.r)),
    r1: clampR(Math.max(anchor.r, focus.r)),
    c0: clampC(Math.min(anchor.c, focus.c)),
    c1: clampC(Math.max(anchor.c, focus.c)),
  };
}

export function inBox(box: RangeBox | null, r: number, c: number): boolean {
  return box !== null && r >= box.r0 && r <= box.r1 && c >= box.c0 && c <= box.c1;
}

/** What is selected: the rows (in screen order) and the columns that go with them. */
export function selectedPart<T extends SheetRow, C extends SheetColumn>(
  selection: SheetSelection,
  rows: readonly T[],
  columns: readonly C[],
  idOf: (row: T) => string,
): { rows: T[]; columns: C[]; box: RangeBox | null } {
  const box = boxOf(selection, rows.length, columns.length);
  if (box) {
    return {
      rows: rows.slice(box.r0, box.r1 + 1),
      columns: columns.slice(box.c0, box.c1 + 1),
      box,
    };
  }
  return {
    rows: rows.filter((row) => selection.rows.has(idOf(row))),
    columns: [...columns],
    box: null,
  };
}

/** Tab-separated with a header line: pastes into Google Sheets one value per cell. */
export function toTsv<T extends SheetRow, C extends SheetColumn>(
  rows: readonly T[],
  columns: readonly C[],
  display: (row: T, column: C) => string,
): string {
  if (rows.length === 0) return "";
  return [
    columns.map((column) => tsvCell(column.label)).join("\t"),
    ...rows.map((row) => columns.map((column) => tsvCell(display(row, column))).join("\t")),
  ].join("\n");
}

/** One total per numeric column — never one sum across different columns. */
export function columnTotals<T extends SheetRow, C extends SheetColumn>(
  rows: readonly T[],
  columns: readonly C[],
  numericKeys: ReadonlySet<string>,
): { column: C; total: number }[] {
  return columns
    .filter((column) => numericKeys.has(column.key))
    .map((column) => {
      let total = 0;
      for (const row of rows) total += asNumber(row[column.key]) ?? 0;
      return { column, total: Math.round(total * 100) / 100 };
    });
}

export interface SelectionChoice {
  value: string;
  label: string;
  /** Shown rows holding this value that are selected. */
  selected: number;
  /** Shown rows holding this value. */
  total: number;
}

/** A column's values as the selection sees them: how many of each value's rows are selected. */
export function selectionChoices<T extends SheetRow>(
  rows: readonly T[],
  key: string,
  selected: ReadonlySet<string>,
  idOf: (row: T) => string,
  labelOf: (value: string) => string = (value) => value,
): SelectionChoice[] {
  const byValue = new Map<string, SelectionChoice>();
  for (const row of rows) {
    const value = cellText(row[key]);
    const choice = byValue.get(value) ?? {
      value,
      label: value === "" ? "(Blank)" : labelOf(value),
      selected: 0,
      total: 0,
    };
    choice.total += 1;
    if (selected.has(idOf(row))) choice.selected += 1;
    byValue.set(value, choice);
  }
  return [...byValue.values()].sort((a, b) =>
    a.value === ""
      ? 1
      : b.value === ""
        ? -1
        : a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
}

/** The ids of the shown rows whose cell holds one of `values`. */
export function idsWithValues<T extends SheetRow>(
  rows: readonly T[],
  key: string,
  values: readonly string[],
  idOf: (row: T) => string,
): string[] {
  const wanted = new Set(values);
  return rows.filter((row) => wanted.has(cellText(row[key]))).map(idOf);
}
